/**
 * Surface Link Badges
 *
 * Renders small clickable pills below assistant messages that created
 * surfaces. Clicking a badge scrolls the canvas panel to the surface.
 */

import { cleanDisplayText } from '@/utils/display-text';
import { useMemo } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { PanelRight } from 'lucide-react';
import { useCanvases } from '../../contexts/CanvasContext';

export function SurfaceLinkBadges() {
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const { surfaces, requestScrollToSurface } = useCanvases();

  const runId = (messageMetadata?.custom as Record<string, unknown> | undefined)?.runId as
    | string
    | undefined;

  const linked = useMemo(() => {
    if (!runId) return [];
    return [...surfaces.values()].filter((s) => s.runId === runId);
  }, [runId, surfaces]);

  if (linked.length === 0) return null;

  return (
    <div className="col-start-1 col-end-[-1] row-start-2 mt-3 flex flex-wrap items-center gap-1.5">
      {linked.map((s) => (
        <button
          key={`${s.canvasId}:${s.surfaceId}`}
          type="button"
          className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          onClick={() => requestScrollToSurface(s.canvasId, s.surfaceId)}
        >
          <PanelRight className="h-3 w-3" />
          {cleanDisplayText(s.title) || 'Surface'}
        </button>
      ))}
    </div>
  );
}
